"use client"

import { useState, useEffect } from "react"
import { Button } from "@repo/ui/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/ui/components/ui/dropdown-menu"
import { Input } from "@repo/ui/components/ui/input"
import { ThemeToggle } from "@repo/ui/components/ui/theme-toggle"
import { Bell, Menu, Search, X } from "lucide-react"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from "@repo/ui/hooks/api/auth/useAuth"
import { useGetCurrentUser } from "../hooks/api/users/use-current-user"
import { useDebounce } from "../hooks/use-debounce"
import { shouldShowCodeSiteSearch } from "../lib/utils/code-site-search-visibility"

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuClick, showMenuButton }: HeaderProps = {}) {
  const { logout } = useAuth();
  const { data: userResp } = useGetCurrentUser();
  const user = (userResp as any)?.data;
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParams.get('code_site') || '');
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const navigate = useNavigate();
  const isCodeSiteSearchVisible = shouldShowCodeSiteSearch(pathname);

  // Update URL when debounced value changes (only on pages that use code_site filter)
  useEffect(() => {
    if (!isCodeSiteSearchVisible) {
      return;
    }
    if (debouncedSearchValue) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('code_site', debouncedSearchValue);
      setSearchParams(newParams, { replace: true });
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('code_site');
      setSearchParams(newParams, { replace: true });
    }
  }, [debouncedSearchValue, isCodeSiteSearchVisible]);

  // Sync search value with URL params when they change externally
  useEffect(() => {
    if (!isCodeSiteSearchVisible) {
      return;
    }
    const urlCodeSite = searchParams.get('code_site') || '';
    if (urlCodeSite !== searchValue) {
      setSearchValue(urlCodeSite);
    }
  }, [searchParams, isCodeSiteSearchVisible]);

  const handleLogout = async () => {
    await logout();
  };

  const initials = `${user?.prenom?.charAt(0) ?? ''}${user?.nom?.charAt(0) ?? ''}`.toUpperCase() || '?';
  const fullName = [user?.prenom, user?.nom].filter(Boolean).join(' ');
  const role: string = user?.role ?? '';

  return (
    <header className="flex items-center justify-between border-b border-border/50 header-glass px-4 py-3 md:px-6 print:hidden relative z-50 shadow-sm">
      <div className="flex items-center gap-4 flex-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="hover:bg-muted shrink-0"
          title="Toggle sidebar"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {isCodeSiteSearchVisible && (
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher par code site..."
              className="pl-10 pr-10 bg-muted/50 border-border/60 focus:bg-background transition-colors"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searchValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2"
                onClick={() => setSearchValue('')}
                title="Effacer la recherche"
                aria-label="Effacer la recherche"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <ThemeToggle />

        <Button
          variant="ghost"
          size="icon"
          className="relative hover:bg-muted"
          title="Notifications"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 text-muted-foreground" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 ring-2 ring-primary/30 ring-offset-1 ring-offset-background"
            >
              <span className="text-sm font-medium">{initials}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {fullName && (
              <>
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-foreground truncate">{fullName}</p>
                  {role && (
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{role}</p>
                  )}
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => navigate('/admin/account')}>
              Profil
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/fiches-interventions">
                Fiches d'interventions
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleLogout()}
              className="text-destructive focus:text-destructive"
            >
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
